import { StrictMode, lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import './index.css'

import ProtectedRoute from './components/ProtectedRoute'
import { Toaster } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import AdminLayout from './components/AdminLayout'
import { PageLoading } from './components/PageLoading'
import React from 'react'
import LandingPage from './pages/LandingPage'
import * as Sentry from "@sentry/react";
import { HelmetProvider } from 'react-helmet-async'
import ErrorPage from './components/ErrorPage'
import posthog from 'posthog-js'
import PostHogPageViewTracker from './components/PostHogPageViewTracker'
import { Outlet } from 'react-router-dom'
import { supabase } from '@/services/supabase'

if (import.meta.env.PROD && import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
    capture_pageview: false, // We use manual tracking in PostHogPageViewTracker
    persistence: 'localStorage',
    person_profiles: 'always',
    autocapture: true,
    debug: import.meta.env.DEV, // Show debug info in console during development
  })
}

// Add auth listener for PostHog to track "Active Users"
if (import.meta.env.PROD) {
  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_IN' && session?.user) {
      posthog.identify(session.user.id, {
        email: session.user.email,
      })
      posthog.capture('user logged in', {
        method: session.user.app_metadata.provider || 'email',
      })
    }
  })
}

if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/ienndcvgnmkvapitnvmi\.supabase\.co/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

// Safe lazy load with retry logic for chunk loading errors
const lazyWithRetry = (componentImport: () => Promise<{ default: React.ComponentType }>) =>
  lazy(async () => {
    const pageHasBeenForceRefreshed = JSON.parse(
      window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
    );

    try {
      return await componentImport();
    } catch (error) {
      if (!pageHasBeenForceRefreshed) {
        window.sessionStorage.setItem('page-has-been-force-refreshed', 'true');
        window.location.reload();
        return { default: () => null }; // Return placeholder while reloading
      }
      throw error;
    }
  });

// Lazy load pages
const PedidosPage = lazyWithRetry(() => import('./pages/client/PedidosPage'))
const DashboardPage = lazyWithRetry(() => import('./pages/admin/DashboardPage'))
const LoginPage = lazyWithRetry(() => import('./pages/admin/LoginPage'))
const MaterialsPage = lazyWithRetry(() => import('./pages/admin/MaterialsPage'))
const SettingsPage = lazyWithRetry(() => import('./pages/admin/SettingsPage'))
const CompleteProfilePage = lazyWithRetry(() => import('./pages/admin/CompleteProfilePage'))
const BusinessConfigPage = lazyWithRetry(() => import('./pages/admin/BusinessConfigPage'))
const InsightsPage = lazyWithRetry(() => import('./pages/admin/InsightsPage'))
const PrivacyPolicyPage = lazyWithRetry(() => import('./pages/legal/PrivacyPolicyPage'))
const TermsOfUsePage = lazyWithRetry(() => import('./pages/legal/TermsOfUsePage'))
const PricingPage = lazyWithRetry(() => import('./pages/admin/PricingPage'))
const SubscriptionPage = lazyWithRetry(() => import('./pages/admin/SubscriptionPage'))
const CheckoutRedirectPage = lazyWithRetry(() => import('./pages/admin/CheckoutRedirectPage'))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
})

const router = createBrowserRouter([
  {
    element: (
      <>
        <PostHogPageViewTracker />
        <Outlet />
      </>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/',
        element: <LandingPage />,
      },
      {
        path: '/login',
        element: <LoginPage />,
      },
      {
        path: '/checkout-redirect',
        element: <ProtectedRoute><CheckoutRedirectPage /></ProtectedRoute>,
      },
      {
        path: '/privacidade',
        element: <PrivacyPolicyPage />,
      },
      {
        path: '/termos',
        element: <TermsOfUsePage />,
      },
      {
        path: '/complete-profile',
        element: <ProtectedRoute><CompleteProfilePage /></ProtectedRoute>,
      },
      {
        path: '/dashboard',
        element: <ProtectedRoute><AdminLayout /></ProtectedRoute>,
        children: [
          {
            index: true,
            element: <DashboardPage />,
          },
          {
            path: 'materiais',
            element: <MaterialsPage />,
          },
          {
            path: 'produtos',
            element: <SettingsPage />,
          },
          {
            path: 'negocio',
            element: <BusinessConfigPage />,
          },
          {
            path: 'insights',
            element: <InsightsPage />,
          },
          {
            path: 'calculadora/:itemId',
            element: <PricingPage />,
          },
          {
            path: 'minha-conta',
            element: <SubscriptionPage />,
          },
        ]
      },
      {
        path: '/pedidos/:slug',
        element: <PedidosPage />,
      },
    ]
  }
])


createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorPage />}>
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <TooltipProvider>
            <Suspense fallback={<PageLoading />}>
              <RouterProvider router={router} />
            </Suspense>
            <Toaster />
          </TooltipProvider>
        </HelmetProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
